/**
 * WorkLifecycleNav — UNIFIED_WORK_INTAKE_TO_GWU_V1
 *
 * Lifecycle navigation component showing where a user is in the governed work lifecycle.
 * This is a read-only orientation surface — not an execution surface.
 *
 * Lifecycle stages:
 *   Guided Flow → Governed Request → GWU → Workspace Inspection → Receipts → Verification → Relationships
 *
 * BOUNDARY:
 * - This is a navigation aid, not an execution trigger
 * - Links carry context (requestId, gwuId) but do not grant authority
 * - Each stage link only appears when that stage is reachable
 */

import React from 'react';
import { ArrowRight, Check, Circle } from 'lucide-react';

export type LifecycleStage =
  | 'guided-flow'
  | 'governed-request'
  | 'gwu'
  | 'workspace-inspection'
  | 'receipts'
  | 'verification'
  | 'relationships';

export interface LifecycleContext {
  requestId?: string;
  gwuId?: string;
  receiptId?: string;
}

interface StageConfig {
  id: LifecycleStage;
  label: string;
  shortLabel: string;
  getHref: (ctx: LifecycleContext) => string | null;
}

const STAGES: StageConfig[] = [
  {
    id: 'guided-flow',
    label: 'Guided Flow',
    shortLabel: 'Flow',
    getHref: () => '/formation-gate/guided-flow',
  },
  {
    id: 'governed-request',
    label: 'Governed Request',
    shortLabel: 'Request',
    getHref: (ctx) => ctx.requestId ? `/requests/${ctx.requestId}` : '/requests',
  },
  {
    id: 'gwu',
    label: 'Work Unit (GWU)',
    shortLabel: 'GWU',
    getHref: (ctx) => ctx.gwuId ? `/formation-gate/gwu-registry?gwuId=${ctx.gwuId}` : '/formation-gate/gwu-registry',
  },
  {
    id: 'workspace-inspection',
    label: 'Workspace Inspection',
    shortLabel: 'Workspace',
    getHref: (ctx) => ctx.gwuId
      ? `/governance-workspace?gwuId=${ctx.gwuId}`
      : '/governance-workspace',
  },
  {
    id: 'receipts',
    label: 'Receipts',
    shortLabel: 'Receipts',
    getHref: (ctx) => ctx.requestId ? `/requests/${ctx.requestId}` : '/verify',
  },
  {
    id: 'verification',
    label: 'Verification',
    shortLabel: 'Verify',
    getHref: (ctx) => ctx.receiptId ? `/verify/${ctx.receiptId}` : '/verify',
  },
  {
    id: 'relationships',
    label: 'Relationships',
    shortLabel: 'Relationships',
    getHref: (ctx) => ctx.gwuId
      ? `/formation-gate/relationship-observability?gwuId=${ctx.gwuId}`
      : '/formation-gate/relationship-observability',
  },
];

interface WorkLifecycleNavProps {
  currentStage: LifecycleStage;
  context: LifecycleContext;
  /** If true, show compact single-line breadcrumb. Default: false (full horizontal trail). */
  compact?: boolean;
  className?: string;
}

export const WorkLifecycleNav: React.FC<WorkLifecycleNavProps> = ({
  currentStage,
  context,
  compact = false,
  className = '',
}) => {
  const currentIndex = STAGES.findIndex(s => s.id === currentStage);

  if (compact) {
    return (
      <nav
        aria-label="Work lifecycle"
        className={`flex items-center gap-1 text-xs text-slate-400 flex-wrap ${className}`}
      >
        {STAGES.map((stage, i) => {
          const href = stage.getHref(context);
          const isActive = stage.id === currentStage;
          const isCompleted = i < currentIndex;

          return (
            <React.Fragment key={stage.id}>
              {i > 0 && (
                <ArrowRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
              )}
              {href && !isActive ? (
                <a
                  href={href}
                  className={`hover:text-slate-200 transition-colors ${
                    isCompleted ? 'text-slate-400' : 'text-slate-600'
                  }`}
                >
                  {stage.shortLabel}
                </a>
              ) : (
                <span className={isActive ? 'text-white font-medium' : 'text-slate-600'}>
                  {stage.shortLabel}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Work lifecycle"
      className={`w-full overflow-x-auto ${className}`}
    >
      <div className="flex items-start gap-0 min-w-max py-2">
        {STAGES.map((stage, i) => {
          const href = stage.getHref(context);
          const isActive = stage.id === currentStage;
          const isCompleted = i < currentIndex;
          const isFuture = i > currentIndex;

          const dotColor = isActive
            ? 'bg-blue-500 ring-2 ring-blue-500/30'
            : isCompleted
            ? 'bg-green-500'
            : 'bg-slate-700';

          const textColor = isActive
            ? 'text-white font-medium'
            : isCompleted
            ? 'text-slate-300'
            : 'text-slate-600';

          return (
            <React.Fragment key={stage.id}>
              <div className="flex flex-col items-center w-28">
                {/* Dot + connector row */}
                <div className="flex items-center w-full">
                  {i > 0 && (
                    <div
                      className={`flex-1 h-px ${isCompleted ? 'bg-green-600' : 'bg-slate-700'}`}
                    />
                  )}
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${dotColor}`}>
                    {isCompleted && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  {i < STAGES.length - 1 && (
                    <div
                      className={`flex-1 h-px ${isCompleted ? 'bg-green-600' : 'bg-slate-700'}`}
                    />
                  )}
                </div>

                {/* Label */}
                <div className="mt-2 text-center px-1">
                  {href && !isActive && !isFuture ? (
                    <a
                      href={href}
                      className={`text-xs hover:text-white transition-colors ${textColor}`}
                    >
                      {stage.shortLabel}
                    </a>
                  ) : href && isActive ? (
                    <span className={`text-xs ${textColor}`}>{stage.shortLabel}</span>
                  ) : (
                    <span className={`text-xs ${textColor}`}>{stage.shortLabel}</span>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

/**
 * WorkLifecycleNextStep — Minimal "next step" prompt after completing a stage.
 * Renders a single forward-navigation CTA.
 */
interface WorkLifecycleNextStepProps {
  currentStage: LifecycleStage;
  context: LifecycleContext;
}

export const WorkLifecycleNextStep: React.FC<WorkLifecycleNextStepProps> = ({
  currentStage,
  context,
}) => {
  const currentIndex = STAGES.findIndex(s => s.id === currentStage);
  const nextStage = STAGES[currentIndex + 1];

  if (!nextStage) return null;

  const href = nextStage.getHref(context);
  if (!href) return null;

  return (
    <a
      href={href}
      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 text-slate-200 text-sm rounded-lg transition-all"
    >
      <span>Continue to {nextStage.label}</span>
      <ArrowRight className="w-4 h-4" />
    </a>
  );
};
